import { useId, useState } from 'react';
import { useListContext, useTranslate } from 'ra-core';
import { DateRangeInput } from '../../components/custom/DateRangeInput';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

type ChecklistDateRangeOption =
  | 'TODAY'
  | 'LAST_7_DAYS'
  | 'LAST_30_DAYS'
  | 'LAST_90_DAYS'
  | 'CUSTOM';

export const DEFAULT_CHECKLIST_DATE_RANGE_OPTION: Exclude<
  ChecklistDateRangeOption,
  'CUSTOM'
> = 'LAST_7_DAYS';

const DATE_RANGE_OPTIONS: {
  label: string;
  value: ChecklistDateRangeOption;
}[] = [
  { label: 'Today', value: 'TODAY' },
  { label: 'Last 7 days', value: 'LAST_7_DAYS' },
  { label: 'Last 30 days', value: 'LAST_30_DAYS' },
  { label: 'Last 90 days', value: 'LAST_90_DAYS' },
  { label: 'Custom', value: 'CUSTOM' },
];

interface ChecklistDateRangeFilterProps {
  label: string;
  alwaysOn?: boolean;
}

export const ChecklistDateRangeFilter = ({
  label,
  alwaysOn: _alwaysOn,
}: ChecklistDateRangeFilterProps) => {
  const { filterValues, setFilters } = useListContext();
  const translate = useTranslate();
  const id = useId();
  const options = DATE_RANGE_OPTIONS.map((option) => ({
    ...option,
    label: translate(`moonstar.checklists.filters.presets.${option.value}`, {
      _: option.label,
    }),
  }));
  const [dateRange, setDateRange] = useState<ChecklistDateRangeOption>(() =>
    getDateRangeOption(filterValues.from, filterValues.to)
  );

  const handleDateRangeChange = (
    nextValue: ChecklistDateRangeOption | null
  ) => {
    if (!nextValue) return;
    setDateRange(nextValue);

    if (nextValue === 'CUSTOM') {
      return;
    }

    setFilters(
      {
        ...filterValues,
        ...getPresetRangeFilter(nextValue),
      },
      undefined,
      false
    );
  };

  return (
    <div className="flex min-w-0 flex-wrap items-end gap-3">
      <div className="grid gap-2">
        <Label
          htmlFor={id}
          className="text-xs leading-none text-muted-foreground"
        >
          {translate(label, { _: label })}
        </Label>
        <Select
          value={dateRange}
          onValueChange={handleDateRangeChange}
          items={options}
        >
          <SelectTrigger id={id} className="h-9 w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Keep both fields registered so preset changes also populate the custom picker. */}
      <div className="w-72 min-w-0 max-w-full" hidden={dateRange !== 'CUSTOM'}>
        <DateRangeInput
          startSource="from"
          endSource="to"
          label={translate('moonstar.checklists.filters.customRange', {
            _: 'Custom range',
          })}
        />
      </div>
    </div>
  );
};

export function getPresetRangeFilter(
  option: Exclude<ChecklistDateRangeOption, 'CUSTOM'>
) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const to = formatDateInputValue(new Date());

  if (option === 'TODAY') {
    return {
      from: formatDateInputValue(today),
      to,
    };
  }

  const daysBack =
    option === 'LAST_7_DAYS' ? 6 : option === 'LAST_30_DAYS' ? 29 : 89;
  const fromDate = new Date(today);
  fromDate.setDate(fromDate.getDate() - daysBack);

  return {
    from: formatDateInputValue(fromDate),
    to,
  };
}

function getDateRangeOption(
  from: unknown,
  to: unknown
): ChecklistDateRangeOption {
  if (typeof from !== 'string' || typeof to !== 'string') {
    return DEFAULT_CHECKLIST_DATE_RANGE_OPTION;
  }

  const presetOptions: Exclude<ChecklistDateRangeOption, 'CUSTOM'>[] = [
    'TODAY',
    'LAST_7_DAYS',
    'LAST_30_DAYS',
    'LAST_90_DAYS',
  ];

  for (const option of presetOptions) {
    const presetRange = getPresetRangeFilter(option);
    if (presetRange.from === from && presetRange.to === to) {
      return option;
    }
  }

  return 'CUSTOM';
}

function formatDateInputValue(date: Date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function pad2(value: number) {
  return String(value).padStart(2, '0');
}
